import React, {Fragment, PureComponent} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames'
import {connect} from 'react-redux'

/* Utils */
import {getAllMatches} from '../ScriptLoader/ScriptLoaderUtils';

/* Constants */
import {
  INLINE_SCRIPT_CODE_INDEX_IN_ARRAY,
  INLINE_SCRIPT_REGEX,
  FIRST_MATCH_INLINE_SCRIPT_REGEX
} from '../../components/ScriptLoader/ScriptLoaderConstants';

/* Constants */
import {FEATURE_TOGGLE_NAMES} from '../../modules/feature-toggle/featureToggleConstants';

/* Components */
import HbIntersectionMonitor from '../../components/Intersection/HbIntersectionMonitor';
import HbPlaceholderFrame from '../../components/Placeholder/frame/HbPlaceholderFrame';
import ScriptLoader from '../../components/ScriptLoader/ScriptLoader';

/* Styles */
import s from '../../views/cmsComponent/view/CmsComponent/CmsComponent.scss'

const INTERSECTION_OBSERVER_ROOT_MARGIN = '100px 0px 100px 0px';

class VoltranFragment extends PureComponent {
  static propTypes = {
    fragmentInfo: PropTypes.object.isRequired,
    renderMethodClientSide: PropTypes.bool,
    render: PropTypes.bool,
    scriptProps: PropTypes.object,
  };

  static defaultProps = {
    scriptProps: {},
  }

  constructor(props) {
    super(props);

    const {
      options,
      renderMethodClientSide,
      render,
    } = props;

    this.state = {
      htmlOptions: {
        render: !options?.onlyClientSide,
      },
      render: options?.scriptsLoadEagerly || render,
      renderMethodClientSide: renderMethodClientSide,
    }
  }

  static getDerivedStateFromProps(props) {
    if (
      props?.options?.componentTypeName === 'HydraRenderingPage' &&
      props.render === true
    ) {
      return {
        render: true
      }
    }

    return null;
  }

  linksRender() {
    const {
      fragmentInfo,
      pageFeatureToggleMap,
      removeClientStyles,
    } = this.props;

    const isCriticalCss = pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.IS_CRITICAL_CSS);

    return (
      <>
        {
          fragmentInfo?.style &&
          [...new Set(fragmentInfo?.style)].map((link, index) => {
            const linkProps = isCriticalCss ? {
              media: 'print',
              onload: 'this.media=\'all\';'
            } : {};

            if (removeClientStyles && link?.href?.includes('client.css')) {
              return null
            }

            return (
              <div
                key={`link_${index}`} dangerouslySetInnerHTML={{
                  __html: `<link rel="${link.rel}" href="${link.href}" ${Object.entries(linkProps).
                    map(([key, value]) => `${key}="${value}"`).
                    join(' ')} />`
                }} />
            );
          })
        }
      </>
    );
  }

  htmlRender() {
    const {
      fragmentInfo,
      options,
      pageFeatureToggleMap,
    } = this.props;

    const htmlClassName = options && (options?.isFullWidth ? 'containerFullWidth' : 'container');
    const html = pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.REMOVE_VOLTRAN_SCRIPTS_INITIAL_LOAD)
      ? fragmentInfo?.html?.replace(FIRST_MATCH_INLINE_SCRIPT_REGEX, '')
      : fragmentInfo?.html

    if(!fragmentInfo?.isEmpty){
      return (
        <div
          className={htmlClassName}
          style={{marginBottom: options?.margin}}
          dangerouslySetInnerHTML={{__html: html}} />
      );
    }
    return null;
  }

  inlineScriptsRender() {
    const {
      fragmentInfo,
      pageFeatureToggleMap,
    } = this.props;

    const {
      renderMethodClientSide,
    } = this.state;

    if (
      !pageFeatureToggleMap?.get(FEATURE_TOGGLE_NAMES.REMOVE_VOLTRAN_SCRIPTS_INITIAL_LOAD) &&
      !renderMethodClientSide
    ) {
      return null
    }

    const matchedScripts = getAllMatches(fragmentInfo?.html, INLINE_SCRIPT_REGEX);

    return Array.from(matchedScripts, script => { //eslint-disable-line
      const inlineScriptCode = script?.groups?.length > 0 ? script?.groups[INLINE_SCRIPT_CODE_INDEX_IN_ARRAY] : false;

      if (!inlineScriptCode) {
        return null;
      }

      return (
        <ScriptLoader
          key={fragmentInfo?.activeComponent?.id}
          children={inlineScriptCode} />
      );
    });
  }

  scriptsRender() {
    const {
      fragmentInfo: {
        scripts,
      },
      scriptProps,
    } = this.props;

    const {
      render,
    } = this.state;

    if (!render) {
      return null;
    }

    return (
      scripts?.map(script => (
        <ScriptLoader
          {...scriptProps}
          allScripts={scripts}
          key={script.src}
          src={script.src} />
      ))
    );
  }

  handleObserverChange = entry => {
    const {
      htmlOptions
    } = this.state;

    if (entry.isIntersecting) {
      this.setState({
        htmlOptions: {
          ...htmlOptions,
          render: true,
        },
        renderMethodClientSide: true,
        render: true,
      })
    }
  };

  renderFragment = () => {
    const {
      ratio,
      skeletonComponent
    } = this.props;

    const {
      htmlOptions,
    } = this.state;

    if (htmlOptions.render) {
      return (
        <>
          {
            this.linksRender()
          }
          {
            this.htmlRender()
          }
          {
            this.inlineScriptsRender()
          }
          {
            this.scriptsRender()
          }
        </>
      )
    } else if (ratio) {
      return (
        <HbPlaceholderFrame
          className={s.placeholder}
          contentClassName={s.placeholderContent}
          ratio={ratio}>
          <div className={s.loading} />
        </HbPlaceholderFrame>
      )
    } else if (skeletonComponent) {
      return skeletonComponent
    } else {
      return null
    }
  }

  render() {
    const {
      className,
      fragmentInfo,
      options,
      isIntersectionObserverDisabled,
    } = this.props;

    const {
      htmlOptions,
      render,
    } = this.state;

    if (!fragmentInfo) {
      return null;
    }

    const intersectionObserverEnabled = (!htmlOptions.render || !render) && !isIntersectionObserverDisabled;

    const WrapperElement = intersectionObserverEnabled
      ? HbIntersectionMonitor
      : Fragment;
    const wrapperProps = intersectionObserverEnabled
      ? {
        rootMargin: INTERSECTION_OBSERVER_ROOT_MARGIN,
        onIntersectionChange: this.handleObserverChange,
        className: cx(className)
      }
      : {};

    return (
      <WrapperElement {...wrapperProps}>
        <div key={fragmentInfo?.activeComponent?.componentName || ''} data-test-id={options?.dataTestId || ''}>
          {
            this.renderFragment()
          }
        </div>
      </WrapperElement>
    );
  }
}

const mapStateToProps = (state) => {
  const {
    featureToggleState: {
      pageFeatureToggleMap,
    },
  } = state;

  return {
    pageFeatureToggleMap,
  }
}

export default connect(mapStateToProps)(VoltranFragment);
